import React, { useState, useMemo } from 'react';
import { X, Search, Check, UserPlus, Loader2 } from 'lucide-react';
import { ChatAvatar } from './ChatAvatar.jsx';
import { isGroupJid } from '../../../lib/sortChats';
import { formatWhatsAppNumber } from './UserProfilePanel.jsx';

export default function WhatsAddParticipantsModal({ groupSubject, existingMemberJids = [], allChats = [], onClose, onAddParticipants }) {
  const [search, setSearch] = useState('');
  const [selectedJids, setSelectedJids] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const existingSet = useMemo(() => {
    return new Set(existingMemberJids.map(j => String(j).split('@')[0]));
  }, [existingMemberJids]);

  // Available contacts not already in the group
  const availableCandidates = useMemo(() => {
    return allChats.filter((c) => {
      if (!c.chat_jid || isGroupJid(c.chat_jid)) return false;
      const phone = String(c.chat_jid).split('@')[0];
      return !existingSet.has(phone);
    });
  }, [allChats, existingSet]);

  const filteredCandidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return availableCandidates;
    return availableCandidates.filter((c) => {
      const name = (c.name || '').toLowerCase();
      const num = formatWhatsAppNumber(c.chat_jid).toLowerCase();
      const jid = String(c.chat_jid).toLowerCase();
      return name.includes(term) || num.includes(term) || jid.includes(term);
    });
  }, [availableCandidates, search]);

  const toggleSelect = (jid) => {
    setSelectedJids((prev) => {
      const next = new Set(prev);
      if (next.has(jid)) next.delete(jid);
      else next.add(jid);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedJids.size === 0) {
      setErrorMsg('Selecciona al menos un contacto para añadir al grupo');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMsg('');
      await onAddParticipants(Array.from(selectedJids));
      onClose();
    } catch (err) {
      setErrorMsg(err.message || 'Error al añadir participantes');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="whats-link-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(3px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        className="whats-link-modal-panel"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '440px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
          padding: 0,
        }}
      >
        {/* Cabecera */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f8fafc',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#dcfce7',
                color: '#15803d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UserPlus size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                Añadir miembros
              </h3>
              <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                {groupSubject || 'Grupo'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              color: '#64748b',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, minHeight: 0 }}>
            {errorMsg && (
              <div
                style={{
                  padding: '10px 14px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#b91c1c',
                  fontSize: '12.5px',
                }}
              >
                {errorMsg}
              </div>
            )}

            {/* Buscador */}
            <div style={{ position: 'relative' }}>
              <Search
                size={15}
                color="#94a3b8"
                style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}
              />
              <input
                type="text"
                placeholder="Buscar contactos para añadir..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={isSubmitting}
                autoFocus
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 32px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '12.5px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Chips seleccionados */}
            {selectedJids.size > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '60px', overflowY: 'auto' }}>
                {Array.from(selectedJids).map((jid) => {
                  const c = allChats.find((item) => item.chat_jid === jid) || {
                    chat_jid: jid,
                    name: formatWhatsAppNumber(jid),
                  };
                  return (
                    <div
                      key={jid}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '3px 8px',
                        backgroundColor: '#dcfce7',
                        border: '1px solid #86efac',
                        borderRadius: '16px',
                        fontSize: '11px',
                        color: '#15803d',
                        fontWeight: '500',
                      }}
                    >
                      <span>{c.name || formatWhatsAppNumber(jid)}</span>
                      <X size={12} style={{ cursor: 'pointer' }} onClick={() => toggleSelect(jid)} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Lista de candidatos */}
            <div
              style={{
                flex: 1,
                minHeight: '160px',
                maxHeight: '260px',
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
              }}
            >
              {filteredCandidates.length === 0 ? (
                <div style={{ padding: '30px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '12.5px' }}>
                  {availableCandidates.length === 0
                    ? 'No hay más contactos disponibles para añadir.'
                    : 'No se encontraron contactos con ese término.'}
                </div>
              ) : (
                filteredCandidates.map((c) => {
                  const isSelected = selectedJids.has(c.chat_jid);
                  return (
                    <div
                      key={c.chat_jid}
                      onClick={() => !isSubmitting && toggleSelect(c.chat_jid)}
                      style={{
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        backgroundColor: isSelected ? '#f0fdf4' : 'transparent',
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        <div style={{ width: '32px', height: '32px', flexShrink: 0 }}>
                          <ChatAvatar url={c.avatar_url} name={c.name} jid={c.chat_jid} isGroup={false} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {c.name || formatWhatsAppNumber(c.chat_jid)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            {formatWhatsAppNumber(c.chat_jid)}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '4px',
                          border: isSelected ? '1px solid #16a34a' : '1px solid #cbd5e1',
                          backgroundColor: isSelected ? '#16a34a' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && <Check size={12} strokeWidth={3} />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Acciones */}
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px',
              backgroundColor: '#f8fafc',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontSize: '12.5px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={isSubmitting || selectedJids.size === 0}
              style={{
                padding: '8px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#16a34a',
                color: '#ffffff',
                fontSize: '12.5px',
                fontWeight: '600',
                cursor: isSubmitting || selectedJids.size === 0 ? 'not-allowed' : 'pointer',
                opacity: isSubmitting || selectedJids.size === 0 ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Añadiendo...</span>
                </>
              ) : (
                <>
                  <UserPlus size={14} />
                  <span>Añadir ({selectedJids.size})</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
